import { CollectionSessionWithUserIds } from '@scale/shared/webinarSessions/types';
import {
  Box,
  Flex,
  Icon,
  Card,
  FormField,
  DatePicker,
  Button,
  Text,
  Badge,
} from '@scale/scaleui-radix';
import {
  addDurationToTimeString,
  parseStartTimeFromDate,
} from 'react-scale/components/WebinarSessions/hooks/calculateFutureOccurrences';
import { TimePicker } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/TimePicker';
import { faBan } from '@fortawesome/pro-regular-svg-icons';
import { UserAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserAutocomplete';
import { isEqual } from 'lodash';

interface ExistingSessionsProps {
  sessions: CollectionSessionWithUserIds[];
  sessionIdsToCancel: { sessionId: string; reason: string }[];
  setSessionIdsToCancel: (sessionIdsToCancel: { sessionId: string; reason: string }[]) => void;
  sessionIdsWithHostChanges: { sessionId: string; hostedBy: string[] }[];
  setSessionIdsWithHostChanges: (
    sessionIdsWithHostChanges: { sessionId: string; hostedBy: string[] }[],
  ) => void;
}

export const ExistingSessions = ({
  sessions,
  sessionIdsToCancel,
  setSessionIdsToCancel,
  sessionIdsWithHostChanges,
  setSessionIdsWithHostChanges,
}: ExistingSessionsProps) => {
  const nonCanceledSessions = sessions.filter(session => !session.canceled);

  if (!nonCanceledSessions.length) {
    return (
      <Flex direction="column" gap="2">
        <Text as="label" size="2" weight="medium">
          Existing session(s)
        </Text>
        <Text size="2">There are no active and upcoming sessions.</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="2">
      <Text as="label" size="2" weight="medium">
        Existing session(s)
      </Text>
      <Flex direction="column" gap="2" overflow="auto" maxHeight="50vh">
        {nonCanceledSessions.map(session => {
          const sessionDate = new Date(session.eventStart);
          const startTime = parseStartTimeFromDate(sessionDate);
          const endTime = addDurationToTimeString(startTime, session.duration);
          const willBeCanceled = sessionIdsToCancel.some(
            sessionToCancel => sessionToCancel.sessionId === session._id,
          );

          return (
            <Card key={session._id} className="min-h-fit">
              <Flex gap="2" direction="column">
                <Flex gap="2" align="center">
                  <FormField.Root size="2">
                    <FormField.Label
                      htmlFor={`webinar-existing-session-date-label-${session._id}`}
                      size="1"
                    >
                      Date
                    </FormField.Label>
                    <DatePicker.Root
                      config={{
                        dates: {
                          mode: 'single',
                          minDate: new Date(),
                        },
                        calendar: {
                          mode: 'fluid',
                        },
                      }}
                      value={[sessionDate]}
                    >
                      <DatePicker.Trigger disabled>
                        <DatePicker.TextField readOnly />
                      </DatePicker.Trigger>
                      <DatePicker.Content>
                        <DatePicker.Header>
                          <DatePicker.Month />
                          <DatePicker.Year />
                        </DatePicker.Header>
                        <DatePicker.Calendar>
                          <DatePicker.DaysLabels>
                            <DatePicker.DayLabel />
                          </DatePicker.DaysLabels>
                          <DatePicker.Days>
                            <DatePicker.Day />
                          </DatePicker.Days>
                        </DatePicker.Calendar>
                        <DatePicker.Actions>
                          <DatePicker.ActionReset />
                          <DatePicker.ActionApply />
                        </DatePicker.Actions>
                      </DatePicker.Content>
                    </DatePicker.Root>
                  </FormField.Root>

                  <FormField.Root size="2">
                    <FormField.Label
                      htmlFor={`webinar-existing-session-start-time-label-${session._id}`}
                      size="1"
                    >
                      Start time
                    </FormField.Label>
                    <TimePicker time={startTime} setTime={() => {}} disabled />
                  </FormField.Root>

                  <FormField.Root size="2">
                    <FormField.Label
                      htmlFor={`webinar-existing-session-end-time-label-${session._id}`}
                      size="1"
                    >
                      End time
                    </FormField.Label>
                    <TimePicker time={endTime} setTime={() => {}} disabled />
                  </FormField.Root>

                  {!willBeCanceled && (
                    <Box pt="4" px="2" height="32px">
                      <Button
                        size="2"
                        variant="ghost"
                        color="neutral"
                        radius="small"
                        className="w-fit"
                        onClick={() => {
                          setSessionIdsToCancel([
                            ...sessionIdsToCancel,
                            { sessionId: session._id, reason: '' },
                          ]);
                        }}
                      >
                        <Icon icon={faBan} size="2" label="cancel" />
                        Cancel session
                      </Button>
                    </Box>
                  )}
                </Flex>

                <Flex direction="column" gap="2">
                  <Text as="label" size="1" weight="medium">
                    Owners
                  </Text>
                  <UserAutocomplete
                    userIds={
                      sessionIdsWithHostChanges.find(h => h.sessionId === session._id)?.hostedBy ??
                      session.hostedBy.map(hydratedUser => hydratedUser._id)
                    }
                    onUserIdsChange={userIds => {
                      // Remove the session from sessionIdsWithHostChanges if the hostedBy is the same
                      if (
                        isEqual(
                          session.hostedBy.map(hydratedUser => hydratedUser._id),
                          userIds,
                        )
                      ) {
                        setSessionIdsWithHostChanges(
                          sessionIdsWithHostChanges.filter(h => h.sessionId !== session._id),
                        );
                        return;
                      }

                      // Otherwise, update the session if it exists or add it if it doesn't
                      const sessionAlreadyChanged = sessionIdsWithHostChanges.some(
                        h => h.sessionId === session._id,
                      );
                      if (sessionAlreadyChanged) {
                        const newSessionIdsWithHostChanges = sessionIdsWithHostChanges.map(h =>
                          h.sessionId === session._id ? { ...h, hostedBy: userIds } : h,
                        );
                        setSessionIdsWithHostChanges(newSessionIdsWithHostChanges);
                      } else {
                        setSessionIdsWithHostChanges([
                          ...sessionIdsWithHostChanges,
                          { sessionId: session._id, hostedBy: userIds },
                        ]);
                      }
                    }}
                    limitToScaliens={false}
                    limitToContributors={true}
                  />
                </Flex>

                {willBeCanceled && (
                  <Badge size="1" color={'warning'} className="w-fit">
                    Will be canceled
                  </Badge>
                )}
              </Flex>
            </Card>
          );
        })}
      </Flex>
    </Flex>
  );
};
